// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { resolveId } from '@medplum/core';
import type { CoverageEligibilityResponse, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { getPayorId } from '../utils/getPayorId';
import { patientDisplayName, patientIdFromCoverageSubject } from '../utils/patient-display';

export interface ActiveCoverageFilters {
  patientName?: string;
  eligibility?: string;
  search?: string;
}

export interface ActiveCoverageEntry {
  cerRef: string;
  patientId: string;
  patientName: string;
  dateChecked: string;
  encounterType: string;
  payorId: string;
  deductibleYTD: string;
  oopMax: string;
  copay: string;
}

export interface UseActiveCoverageReturn {
  entries: ActiveCoverageEntry[];
  loading: boolean;
  total: number;
}

const BENEFIT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/benefit-type';

/** Match `useHSATDevices` — batch `_id` queries need an explicit count or the server may page (~20). */
const BATCH_RESOURCE_SEARCH_COUNT = '1000';

function formatMoney(value: number | undefined): string {
  if (value == null) {
    return '—';
  }
  return `$${value.toLocaleString('en-US')}`;
}

function extractBenefit(cer: CoverageEligibilityResponse, typeCode: string): string {
  const items = cer.insurance?.[0]?.item ?? [];
  for (const item of items) {
    for (const benefit of item.benefit ?? []) {
      if (benefit.type?.coding?.some((c) => c.system === BENEFIT_TYPE_SYSTEM && c.code === typeCode)) {
        const allowed = benefit.allowedMoney?.value;
        const used = benefit.usedMoney?.value;
        if (typeCode === 'copay') {
          return formatMoney(allowed ?? used);
        }
        return `${formatMoney(used)} / ${formatMoney(allowed)}`;
      }
    }
  }
  return '—';
}

export function useActiveCoverage({
  page,
  pageSize,
  filters,
}: {
  page: number;
  pageSize: number;
  filters: ActiveCoverageFilters;
}): UseActiveCoverageReturn {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<ActiveCoverageEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const patientNameFilter = filters.patientName?.toLowerCase() ?? '';
  const searchFilter = filters.search?.toLowerCase() ?? '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const cers = await medplum.searchResources('CoverageEligibilityResponse', {
        outcome: 'complete',
        _sort: '-created',
        _count: '200',
      });

      const inforce = cers.filter((cer) => cer.insurance?.[0]?.inforce === true);

      const patientIds = [...new Set(inforce.map((cer) => patientIdFromCoverageSubject(cer.patient)).filter(Boolean))];
      const insurerIds = [...new Set(inforce.map((cer) => resolveId(cer.insurer)).filter(Boolean) as string[])];

      const [patientResources, orgResources] = await Promise.all([
        patientIds.length > 0
          ? medplum.searchResources('Patient', { _id: patientIds.join(','), _count: BATCH_RESOURCE_SEARCH_COUNT })
          : Promise.resolve([] as Patient[]),
        insurerIds.length > 0
          ? medplum.searchResources('Organization', {
              _id: insurerIds.join(','),
              _count: BATCH_RESOURCE_SEARCH_COUNT,
            })
          : Promise.resolve([] as Organization[]),
      ]);

      const patientMap: Record<string, Patient> = {};
      for (const p of patientResources) {
        if (p.id) {
          patientMap[`Patient/${p.id}`] = p;
        }
      }

      const orgMap: Record<string, Organization> = {};
      for (const o of orgResources) {
        if (o.id) {
          orgMap[`Organization/${o.id}`] = o;
        }
      }

      const allEntries: ActiveCoverageEntry[] = inforce.map((cer) => {
        const pid = patientIdFromCoverageSubject(cer.patient);
        const patient = pid ? patientMap[`Patient/${pid}`] : undefined;
        const oid = resolveId(cer.insurer);
        const org = oid ? orgMap[`Organization/${oid}`] : undefined;
        const patientName = patientDisplayName(patient, cer.patient);

        return {
          cerRef: cer.id ?? '',
          patientId: pid,
          patientName,
          dateChecked: cer.created ?? '',
          encounterType: '—',
          payorId: getPayorId(org),
          deductibleYTD: extractBenefit(cer, 'deductible'),
          oopMax: extractBenefit(cer, 'out-of-pocket'),
          copay: extractBenefit(cer, 'copay'),
        };
      });

      const filtered = allEntries.filter((e) => {
        if (patientNameFilter && !e.patientName.toLowerCase().includes(patientNameFilter)) {
          return false;
        }

        if (searchFilter) {
          if (!e.patientName.toLowerCase().includes(searchFilter) && !e.payorId.toLowerCase().includes(searchFilter)) {
            return false;
          }
        }

        return true;
      });

      setTotal(filtered.length);
      setEntries(filtered.slice((page - 1) * pageSize, page * pageSize));
    } finally {
      setLoading(false);
    }
  }, [medplum, page, pageSize, patientNameFilter, searchFilter]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [fetchData]);

  return { entries, loading, total };
}
